import { reactive } from 'vue'

/**
 * Промис-подтверждение поверх shadcn `AlertDialog`.
 *
 * Точка вызова выглядит так:
 *
 * ```ts
 * const { confirm } = useConfirm()
 * if (!await confirm({ title: 'Удалить пользователя?', destructive: true })) return
 * await users.remove(id)
 * ```
 *
 * Почему модульный синглтон, а не `<ConfirmDialog v-model:open>` в каждой вью:
 * подтверждение — это ответ на **действие**, а не состояние экрана. С синглтоном
 * место принятия решения остаётся внутри обработчика (`if (!await confirm(...)) return`),
 * а не размазывается по `ref`-ам, коллбэкам и `@confirm`-эмитам соседнего компонента.
 * Цена — ровно один смонтированный `<ConfirmDialog />` на приложение.
 */
export interface ConfirmOptions {
  title: string
  description?: string
  /** По умолчанию «Подтвердить». */
  confirmText?: string
  /** По умолчанию «Отмена». */
  cancelText?: string
  /** Красит кнопку подтверждения в destructive — для удаления и отзыва сессий. */
  destructive?: boolean
}

const DEFAULT_CONFIRM_TEXT = 'Подтвердить'
const DEFAULT_CANCEL_TEXT = 'Отмена'

/**
 * Состояние единственного диалога. Экспортируется только ради `ConfirmDialog.vue`
 * — прикладной код работает через `useConfirm()`.
 */
export const confirmState = reactive({
  open: false,
  title: '',
  description: '',
  confirmText: DEFAULT_CONFIRM_TEXT,
  cancelText: DEFAULT_CANCEL_TEXT,
  destructive: false,
})

/**
 * Держим `resolve` вне `reactive`: функция-резолвер не должна попадать
 * в реактивный прокси — оборачивать её не за чем, а лишние срабатывания эффектов вредны.
 */
let resolveCurrent: ((value: boolean) => void) | null = null

/**
 * Закрывает диалог и разрешает висящий промис. Идемпотентна: повторный вызов
 * (например, `AlertDialogAction` закрыл диалог сам и следом прилетел `update:open(false)`)
 * не разрешает промис дважды.
 */
export function resolveConfirm(value: boolean): void {
  const resolve = resolveCurrent
  resolveCurrent = null
  confirmState.open = false
  resolve?.(value)
}

function confirm(options: ConfirmOptions): Promise<boolean> {
  // Второй `confirm()` поверх открытого первого: старый честно разрешаем как отказ,
  // иначе его промис повис бы навсегда и `await` в вызывающем коде никогда бы не вернулся.
  resolveConfirm(false)

  confirmState.title = options.title
  confirmState.description = options.description ?? ''
  confirmState.confirmText = options.confirmText ?? DEFAULT_CONFIRM_TEXT
  confirmState.cancelText = options.cancelText ?? DEFAULT_CANCEL_TEXT
  confirmState.destructive = options.destructive ?? false
  confirmState.open = true

  return new Promise<boolean>((resolve) => {
    resolveCurrent = resolve
  })
}

export function useConfirm() {
  return { confirm }
}
